import { useMutation, useQuery } from '@apollo/client';
import { Spacer, Text } from '@loomhq/lens';
import React, { useEffect, useRef } from 'react';

import { GeneratedMetadataModel } from 'constants/discovery';
import {
  VIDEO_TAG_SUGGESTION_DISPOSITIONED,
  VIDEO_TAG_SUGGESTION_SHOWN,
} from 'constants/events';
import { ROLLOUT_GENERATED_METADATA } from 'constants/featureFlag';
import { track } from 'utilities/analytics';

import { useFeatureFlagValue } from 'hooks/featureFlag';

import DeclineSuggestedVideoTag from './DeclineSuggestedVideoTag.graphql';
import GetDeclinedSuggestedVideoTags from './GetDeclinedSuggestedVideoTags.graphql';
import GetGeneratedVideoMetadata from './GetGeneratedVideoMetadata.graphql';
import AddTagToVideoMutation from '../AddTagToVideoMutation.graphql';
import TagPills from '../read-only-tag-pills';

const RECOMMENDATION_TYPE = 'model';

type GeneratedMetadataProps = {
  videoId: string;
  existingTags: string[];
  userCanEditTags?: boolean;
};

// Get only the suggested tags that are eligible to be shown,
// as well as the list of tags that have been declined by the user already
const useFilteredTags = (
  videoId: string,
  existingTags: string[],
  {
    tagModel,
    descriptionModel,
  }: {
    tagModel: GeneratedMetadataModel | null;
    descriptionModel: GeneratedMetadataModel | null;
  }
): [any, string[]] => {
  const { data: declinedTagsData } = useQuery(GetDeclinedSuggestedVideoTags, {
    variables: {
      videoId,
    },
    skip: !tagModel,
  });

  const declinedTags = declinedTagsData?.getDeclinedSuggestedVideoTags?.tags;

  const { data } = useQuery(GetGeneratedVideoMetadata, {
    variables: {
      videoId,
      tagModel,
      descriptionModel,
      includeTagModel: Boolean(tagModel),
      includeDescriptionModel: Boolean(descriptionModel),
    },
  });

  // Only show suggested tags which aren't being used or have been declined already
  const ignoredTagsSet = new Set([...existingTags, ...(declinedTags || [])]);
  const filteredTags =
    data?.tagMetadata?.tags?.filter(tag => !ignoredTagsSet.has(tag)) || [];

  return [data, filteredTags];
};

export default function GeneratedMetadata({
  videoId,
  existingTags,
  userCanEditTags = false,
}: GeneratedMetadataProps): JSX.Element {
  const [addTagToVideo] = useMutation(AddTagToVideoMutation, {
    refetchQueries: ['GetTagsByVideoId'],
  });

  const [declineSuggestedVideoTag] = useMutation(DeclineSuggestedVideoTag, {
    refetchQueries: ['GetDeclinedSuggestedVideoTags'],
  });
  const { tagModel, descriptionModel } =
    useFeatureFlagValue(ROLLOUT_GENERATED_METADATA) ?? {};
  const [data, filteredTags] = useFilteredTags(videoId, existingTags, {
    tagModel,
    descriptionModel,
  });

  const loggedTagShownEvent = useRef(false);

  useEffect(() => {
    if (loggedTagShownEvent.current || !filteredTags.length) {
      return;
    }

    loggedTagShownEvent.current = true;

    filteredTags.forEach((tag, index) => {
      track(VIDEO_TAG_SUGGESTION_SHOWN, {
        video_id: videoId,
        tag,
        tag_suggestion_position: index,
        recommendation_type: RECOMMENDATION_TYPE,
        ml_model: tagModel,
      });
    });
  }, [videoId, filteredTags, tagModel]);

  const onRemoveTag = tag => {
    declineSuggestedVideoTag({ variables: { videoId, tag } });

    track(VIDEO_TAG_SUGGESTION_DISPOSITIONED, {
      video_id: videoId,
      tag,
      is_accepted: false,
      ml_model: tagModel,
    });
  };

  const onAddTag = tag => {
    addTagToVideo({
      variables: {
        videoId,
        tag,
        context: 'Share page suggested tags',
      },
    });

    track(VIDEO_TAG_SUGGESTION_DISPOSITIONED, {
      video_id: videoId,
      tag,
      is_accepted: true,
      ml_model: tagModel,
    });
  };

  return (
    <>
      {filteredTags.length ? (
        <>
          <Text fontWeight="medium">Suggested tags</Text>
          <div className="flex">
            {userCanEditTags && <Spacer right="12px" />}
            <TagPills
              tags={filteredTags}
              backgroundColor="backgroundSecondary2"
              onAddTag={userCanEditTags && onAddTag}
              onRemoveTag={userCanEditTags && onRemoveTag}
            />
          </div>
        </>
      ) : null}
      {data?.descriptionMetadata?.description ? (
        <div className={`my:small`}>
          <Text fontWeight="medium">Suggested description</Text>
          <div>&quot;{data.descriptionMetadata.description}&quot;</div>
        </div>
      ) : null}
    </>
  );
}
